import React, { useRef, useEffect, useState } from 'react';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QuotationPage } from './types';

interface QuotationPageNavigationProps {
  linearList: Array<
    | { type: 'page'; page: QuotationPage }
    | { type: 'group'; group: { id: string; name: string; pages: QuotationPage[] } }
  >;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  usePageDropdown: boolean;
  additionalTabs?: Array<{
    id: string;
    label: string;
    condition?: boolean;
    customLabel?: string;
  }>;
}

export function QuotationPageNavigation({
  linearList = [],
  activeTab,
  setActiveTab,
  usePageDropdown,
  additionalTabs = [],
}: QuotationPageNavigationProps) {
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showTabArrows, setShowTabArrows] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

  // Ensuring all pages are visible (double-checking the filter from QuotationView)
  const visiblePages = linearList.filter(item => item.type === 'page').map(item => item.page);

  // Determinar quais páginas devem ser mostradas como tabs
  const pagesAsTabsArray = linearList.filter(item => item.type === 'page').map(item => item.page);
  // Se houver grupos, dropdown é agrupado; senão, mantém padrão
  const pagesInDropdownArray = linearList.filter(item => item.type === 'group').map(item => item.group);

  // Obter a primeira página do dropdown (para uso como default)
  const firstDropdownPage = pagesInDropdownArray.length > 0 ? pagesInDropdownArray[0].pages[0] : null;
  
  // Função para lidar com o clique no botão do dropdown
  const handleDropdownClick = (e: React.MouseEvent) => {
    // Prevenir o comportamento padrão para controlar manualmente
    e.preventDefault();
    e.stopPropagation();
    
    // Se o dropdown já estiver aberto, fechá-lo
    if (dropdownOpen) {
      setDropdownOpen(null);
      return;
    }
    
    // Determinar qual página usar ao clicar
    let targetPageId = null;
    
    // Se não houver página selecionada anteriormente, usar a primeira página
    if (!targetPageId && firstDropdownPage) {
      targetPageId = firstDropdownPage.id;
    }
    
    // Se tivermos uma página alvo e não estivermos navegando para ela atualmente
    if (targetPageId && activeTab !== `page-${targetPageId}`) {
      // Encontrar a página pelo ID
      const targetPage = linearList.find(item => item.type === 'page' && item.page.id === targetPageId);
      
      if (targetPage) {
        // Navegar para a página alvo
        setActiveTab(`page-${targetPageId}`);
        // Atualizar o título ativo
        // setActivePageTitle(targetPage.title || `Página ${pages.indexOf(targetPage) + 1}`); // This line is removed
        // Atualizar a última página selecionada
        // setLastSelectedDropdownPageId(targetPageId); // This line is removed
      }
    }
    
    // Abrir o dropdown
    const foundGroup = linearList.find(item => item.type === 'group' && item.group.pages.some(p => p.id === targetPageId));
    setDropdownOpen(dropdownOpen === null ? (foundGroup && foundGroup.type === 'group' ? foundGroup.group.id : null) : null);
  };

  // Função para verificar se alguma página do dropdown está atualmente ativa
  const isDropdownPageActive = () => {
    return activeTab.startsWith('page-') && pagesInDropdownArray.some(group => group.pages.some(p => `page-${p.id}` === activeTab));
  };

  // Função para verificar se uma página específica está realmente ativa (sendo exibida)
  const isPageActuallyActive = (pageId: string) => {
    return activeTab === `page-${pageId}`;
  };

  // Atualizar o título da página ativa e rastrear a última página selecionada quando o activeTab mudar
  useEffect(() => {
    if (activeTab.startsWith('page-')) {
      const pageId = activeTab.replace('page-', '');
      const page = linearList.find(item => item.type === 'page' && item.page.id === pageId);
      
      if (page) {
        // Verificar se a página ativa está no dropdown
        const isPageInDropdown = pagesInDropdownArray.some(group => group.pages.some(p => p.id === pageId));
        
        if (isPageInDropdown) {
          // Se a página estiver no dropdown, mostrar seu título e salvar como última selecionada
          // setActivePageTitle(page.title || `Página ${pages.indexOf(page) + 1}`); // This line is removed
          // setLastSelectedDropdownPageId(pageId); // This line is removed
        } else if (usePageDropdown) {
          // Se a página não estiver no dropdown (está como tab individual),
          // manter o título da última página selecionada, se houver
          // Não atualizamos o título aqui para preservar a última seleção
        }
      }
    }
    // Não atualizamos o título quando não estamos em uma página (ex: boas-vindas, hospedagens)
    // para preservar a última página do dropdown selecionada
  }, [activeTab, linearList, pagesInDropdownArray, usePageDropdown]);

  // Inicializar o lastSelectedDropdownPageId quando firstDropdownPage mudar
  useEffect(() => {
    if (firstDropdownPage && !linearList.find(item => item.type === 'group' && item.group.pages.some(p => p.id === firstDropdownPage.id))) {
      // setLastSelectedDropdownPageId(firstDropdownPage.id); // This line is removed
    }
  }, [firstDropdownPage, linearList]);

  // Função utilitária para obter o grupo de uma página
  const getGroupForPage = (pageId: string) => {
    if (!linearList || linearList.length === 0) return null;
    return linearList.find(item => item.type === 'group' && item.group.pages.some(p => p.id === pageId));
  };

  // Novo: label do dropdown deve ser o nome do grupo se a página ativa estiver em um grupo
  const getDropdownButtonTitle = () => {
    if (isDropdownPageActive()) {
      const pageId = activeTab.replace('page-', '');
      const group = getGroupForPage(pageId);
      if (group && group.type === 'group') return group.group.name;
    }
    // Se não houver página ativa, usar o grupo da última página selecionada
    // if (lastSelectedDropdownPageId) { // This line is removed
    //   const group = getGroupForPage(lastSelectedDropdownPageId); // This line is removed
    //   if (group) return group.name; // This line is removed
    // } // This line is removed
    // Fallback: nome do primeiro grupo
    if (linearList && linearList.length > 0) {
      const firstGroup = linearList.find(item => item.type === 'group');
      if (firstGroup && firstGroup.type === 'group') return firstGroup.group.name;
    }
    // Fallback para a primeira página ou texto genérico
    const fallbackPageItem = firstDropdownPage ? linearList.find(item => item.type === 'page' && item.page.id === firstDropdownPage.id) : null;
    const fallbackPageTitle = fallbackPageItem && fallbackPageItem.type === 'page' ? fallbackPageItem.page.title : '?';
    return firstDropdownPage 
      ? (firstDropdownPage.title || `Página ${fallbackPageTitle}`)
      : "Mais Páginas";
  };

  // Verificar overflow nas tabs
  useEffect(() => {
    const checkTabsOverflow = () => {
      if (tabsListRef.current) {
        const { scrollWidth, clientWidth } = tabsListRef.current;
        const hasOverflow = scrollWidth > clientWidth;
        setShowTabArrows(hasOverflow);
        
        if (hasOverflow) {
          const isAtStart = tabsListRef.current.scrollLeft <= 10;
          const isAtEnd = tabsListRef.current.scrollLeft + clientWidth >= scrollWidth - 10;
          
          const container = tabsListRef.current.closest('.tabs-container');
          if (container) {
            container.classList.toggle('at-start', isAtStart);
            container.classList.toggle('at-end', isAtEnd);
          }
        }
      }
    };
    
    checkTabsOverflow();
    
    window.addEventListener('resize', checkTabsOverflow);
    
    return () => {
      window.removeEventListener('resize', checkTabsOverflow);
    };
  }, [linearList]);

  // Scroll para a tab ativa quando ela mudar
  useEffect(() => {
    if (tabsListRef.current && activeTab) {
      // Skip scrolling to page tabs when using dropdown navigation and the active tab is in dropdown
      if (usePageDropdown && activeTab.startsWith('page-') && !linearList.some(item => item.type === 'page' && `page-${item.page.id}` === activeTab)) {
        return;
      }
      
      const activeTabElement = tabsListRef.current.querySelector(`[data-value="${activeTab}"]`);
      if (activeTabElement) {
        // Calculate the position to scroll to center the active tab
        const tabsContainer = tabsListRef.current;
        const tabElement = activeTabElement as HTMLElement;
        const containerWidth = tabsContainer.clientWidth;
        const tabOffsetLeft = tabElement.offsetLeft;
        const tabWidth = tabElement.offsetWidth;
        
        // Center the tab in the container
        const scrollPosition = tabOffsetLeft - (containerWidth / 2) + (tabWidth / 2);
        
        tabsContainer.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }, [activeTab, usePageDropdown, linearList]);

  // Ajustar a navegação quando o modo de exibição (dropdown/tabs) mudar
  useEffect(() => {
    // Se o modo de exibição mudar e a página atual estiver no dropdown ou nas tabs individuais
    if (activeTab.startsWith('page-')) {
      const pageId = activeTab.replace('page-', '');
      const page = linearList.find(item => item.type === 'page' && item.page.id === pageId);
      
      if (page && page.type === 'page') {
        // Se o dropdown estiver ativo e a página não deveria ser excluída do dropdown
        if (usePageDropdown && !page.page.excludeFromDropdown) {
          // A página deve estar no dropdown, atualizar o título do dropdown
          // setActivePageTitle(page.page.title || `Página ${linearList.indexOf(page) + 1}`); // This line is removed
        }
      }
    } else if (usePageDropdown && firstDropdownPage) {
      // Se nenhuma página estiver ativa, definir o título para a primeira página
      // setActivePageTitle(firstDropdownPage.title || `Página ${linearList.indexOf(firstDropdownPage) + 1}`); // This line is removed
    }
    
    // Após qualquer mudança no modo de exibição, verificar o overflow
    setTimeout(() => {
      if (tabsListRef.current) {
        const { scrollWidth, clientWidth } = tabsListRef.current;
        setShowTabArrows(scrollWidth > clientWidth);
      }
    }, 100);
  }, [usePageDropdown, linearList, activeTab, firstDropdownPage]);

  // Inicializar para a primeira página do dropdown na montagem do componente
  useEffect(() => {
    // Verificar se há páginas no dropdown e o dropdown está ativo
    if (usePageDropdown && pagesInDropdownArray.length > 0 && firstDropdownPage) {
      // Verificar se alguma página está ativa
      const isAnyPageActive = activeTab.startsWith('page-');
      
      if (!isAnyPageActive) {
        // Se o usuário estiver em uma aba que não é uma página (como "boas-vindas"),
        // não queremos redirecionar, apenas inicializar o lastSelectedDropdownPageId
        // se ainda não estiver definido
        // if (!lastSelectedDropdownPageId) { // This line is removed
        //   setLastSelectedDropdownPageId(firstDropdownPage.id); // This line is removed
        // } // This line is removed
      } else {
        // Se estiver em uma página, verificar se a página existe
        const pageId = activeTab.replace('page-', '');
        const pageExists = linearList.some(item => item.type === 'page' && item.page.id === pageId);
        
        // Só redirecionar se a página não existir
        if (!pageExists) {
          // Usar a última página selecionada ou a primeira
          const targetPageItem = linearList.find(item => item.type === 'page' && item.page.id === pageId);
          const targetPageId = targetPageItem && targetPageItem.type === 'page' ? targetPageItem.page.id : null;
          const targetPage = targetPageId ? linearList.find(item => item.type === 'page' && item.page.id === targetPageId) : null;
          
          if (targetPage) {
            setActiveTab(`page-${targetPageId}`);
            // setActivePageTitle(targetPage.title || `Página ${linearList.indexOf(targetPage) + 1}`); // This line is removed
            // setLastSelectedDropdownPageId(targetPageId); // This line is removed
          }
        }
      }
    }
  // Este efeito deve rodar apenas na montagem do componente
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buttons de navegação lateral não precisam de prevenção de eventos
  const handleScrollLeft = () => {
    if (tabsListRef.current) {
      tabsListRef.current.scrollLeft -= 200;
    }
  };

  const handleScrollRight = () => {
    if (tabsListRef.current) {
      tabsListRef.current.scrollLeft += 200;
    }
  };

  return (
    <div className="relative flex items-center tabs-container w-full">
      {showTabArrows && (
        <button 
          className="absolute left-2 z-10 p-1 bg-white/80 hover:bg-white rounded-full shadow-md text-voah-teal"
          onClick={handleScrollLeft}
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      )}
      
      <TabsList 
        ref={tabsListRef}
        className={`w-full flex h-[52px] items-center justify-start bg-white border-b overflow-x-auto scrollbar-hide text-xs sm:text-sm md:text-base max-w-[100vw] lg:max-w-none ${showTabArrows ? 'px-12 sm:px-14' : 'px-2 sm:px-4'}`}
      >
        {/* Primeira tab (Boas Vindas) */}
        <TabsTrigger 
          value="boas-vindas" 
          className="px-2 sm:px-4 py-1 font-medium text-gray-600 data-[state=active]:text-voah-teal relative flex items-center h-full data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:bottom-[2px] data-[state=active]:after:h-0.5 data-[state=active]:after:bg-orange-500 mt-0"
        >
          Boas Vindas
        </TabsTrigger>
        {/* Renderização linear: tabs e grupos na ordem */}
        {linearList.map((item, idx) => {
          if (item.type === 'page') {
            return (
              <TabsTrigger
                key={`page-${item.page.id}`}
                value={`page-${item.page.id}`}
                className="px-2 sm:px-4 py-1 font-medium text-gray-600 data-[state=active]:text-voah-teal relative flex items-center h-full data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:bottom-[2px] data-[state=active]:after:h-0.5 data-[state=active]:after:bg-orange-500 mt-0"
              >
                {item.page.title || `Página`}
              </TabsTrigger>
            );
          } else if (item.type === 'group') {
            return (
              <div key={`group-${item.group.id}`} className="px-2 sm:px-4 py-1 relative h-full flex items-center mt-0">
                <Popover open={dropdownOpen === item.group.id} onOpenChange={open => setDropdownOpen(open ? item.group.id : null)}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className={cn(
                        "flex items-center gap-1 font-medium px-2 sm:px-4 py-1 relative h-full mt-[-1px]",
                        item.group.pages.some(p => activeTab === `page-${p.id}`)
                          ? "text-voah-teal after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-[2px] after:h-0.5 after:bg-orange-500" 
                          : "text-gray-600 hover:text-voah-teal"
                      )}
                      onClick={e => {
                        e.preventDefault();
                        setDropdownOpen(dropdownOpen === item.group.id ? null : item.group.id);
                      }}
                    >
                      {item.group.name}
                      <ChevronDown className="h-4 w-4 opacity-70" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0" align="start">
                    <Command>
                      <CommandList>
                        <CommandEmpty>Nenhuma página encontrada</CommandEmpty>
                        <CommandGroup>
                          {item.group.pages.map(page => (
                            <CommandItem
                              key={page.id}
                              onSelect={() => {
                                setActiveTab(`page-${page.id}`);
                                setDropdownOpen(null);
                              }}
                              className={cn(
                                "flex items-center gap-2 !bg-white hover:!bg-gray-100 transition-colors",
                                isPageActuallyActive(page.id) && "!bg-orange-50 !text-voah-teal hover:!bg-orange-100",
                              )}
                            >
                              {isPageActuallyActive(page.id) && <Check className="h-4 w-4 text-voah-teal" />}
                              <span className={cn(
                                "transition-colors",
                                isPageActuallyActive(page.id) ? "!text-voah-teal !font-medium" : "!text-gray-700"
                              )}>{page.title || `Página`}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            );
          }
          return null;
        })}
        {/* Tabs adicionais (hospedagens, voos, etc) - serão exibidos por último */}
        {additionalTabs.map((tab) => {
          if (tab.condition === false) return null;
          return (
            <TabsTrigger 
              key={tab.id}
              value={tab.id} 
              className="px-2 sm:px-4 py-1 font-medium text-gray-600 data-[state=active]:text-voah-teal relative flex items-center h-full data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:bottom-[2px] data-[state=active]:after:h-0.5 data-[state=active]:after:bg-orange-500 mt-0"
            >
              {tab.customLabel || tab.label}
            </TabsTrigger>
          );
        })}
      </TabsList>
      
      {showTabArrows && (
        <button 
          className="absolute right-2 z-10 p-1 bg-white/80 hover:bg-white rounded-full shadow-md text-voah-teal"
          onClick={handleScrollRight}
        >
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      )}
    </div>
  );
} 